import { AbstractMesh, Vector3 } from '@babylonjs/core';
import { AbstractMeshBehavior } from './AbstractMeshBehavior';

export interface MovingPlatformConfig {
  /**
   * Start position of the platform (moving platform)
   */
  start: Vector3;

  /**
   * End position of the platform (moving platform)
   */
  end: Vector3;

  /**
   * Speed of the platform (moving platform)
   */
  speed: number;

  /**
   * Direction of the platform (moving platform)
   */
  direction?: number;
}

export class MovingPlatformBehavior extends AbstractMeshBehavior {
  // Movement state
  private progress: number = 0;
  private direction: number;

  // Velocity for player to use
  public velocity: Vector3 = Vector3.Zero();

  constructor(
    public start: Vector3,
    public end: Vector3,
    public speed: number,
    direction: number = 1
  ) {
    super('movingPlatform');
    this.direction = direction;
  }

  attach(mesh: AbstractMesh): void {
    super.attach(mesh);

    // Set initial position to start
    this.mesh.position.copyFrom(this.start);

    // Store reference in mesh metadata for easy access
    this.setMetaData('movingPlatform', this);
  }

  beforeRender(): void {
    if (!this.mesh || !this.level) return;

    // Get deltaTime from engine
    const deltaTime = this.level.game.scene.getEngine().getDeltaTime() / 1000;
    if (deltaTime <= 0) return;

    const oldPos = this.mesh.position.clone();

    // Update progress
    this.progress += this.speed * deltaTime * this.direction;

    // Reverse direction at ends
    if (this.progress >= 1) {
      this.progress = 1;
      this.direction = -1;
    } else if (this.progress <= 0) {
      this.progress = 0;
      this.direction = 1;
    }

    // Calculate new position
    const newPos = Vector3.Lerp(this.start, this.end, this.progress);

    // Calculate velocity (for player riding the platform)
    this.velocity = newPos.subtract(oldPos).scale(1 / deltaTime);

    // Update mesh position
    this.mesh.position.copyFrom(newPos);

    // Sync physics body with mesh using disablePreStep pattern
    // This is the recommended way to sync ANIMATED bodies in Babylon.js
    if (this.mesh.physicsBody) {
      this.mesh.physicsBody.disablePreStep = false;
    }

    // Check if player is on this platform
    this.checkPlayerOnPlatform();
  }

  private checkPlayerOnPlatform(): void {
    const player = this.level.player;
    if (!player) return;

    const playerPos = player.getPosition();
    const isOnTop = this.isPositionOnTop(playerPos);

    if (isOnTop && player.grounded) {
      // Player is on this platform
      player.currentPlatform = this;
    } else if (player.currentPlatform === this && !isOnTop) {
      // Player left this platform
      player.currentPlatform = null;
    }
  }

  /**
   * Get platform info for player movement
   */
  getPlatformInfo(): { velocity: Vector3; topY: number } {
    const bounds = this.mesh.getBoundingInfo().boundingBox;
    return {
      velocity: this.velocity.clone(),
      topY: bounds.maximumWorld.y,
    };
  }

  /**
   * Check if a position is on top of this platform
   */
  isPositionOnTop(position: Vector3, tolerance: number = 0.5): boolean {
    const bounds = this.mesh.getBoundingInfo().boundingBox;
    const min = bounds.minimumWorld;
    const max = bounds.maximumWorld;

    return (
      position.x >= min.x - tolerance &&
      position.x <= max.x + tolerance &&
      position.z >= min.z - tolerance &&
      position.z <= max.z + tolerance &&
      position.y >= max.y - tolerance &&
      position.y <= max.y + 2
    );
  }

  clone(): MovingPlatformBehavior {
    return new MovingPlatformBehavior(this.start.clone(), this.end.clone(), this.speed, this.direction);
  }
}
